import React from 'react';
import compose from 'recompose/compose';
import { connect } from 'react-redux';
import { ApplicationState } from '../../../store';
import Typography from '@mui/material/Typography';
import { actionCreators } from '../../../store/ConversationSupervisorStore';
import { getActiveLeapClaims, hasSelectedDebtorClaims, getEligibleActiveLeapClaimsForCurrentStep } from '../../../store/Selectors';
import { Step } from './ConversationSupervisorStep';
import StepZilla from 'react-stepzilla';
import ConversationStart from './ConversationStart';
import OtherReason from './steps/OtherReason';
import ConversationSummary from '../containers/ConversationSummary';
import CircularProgress from '@mui/material/CircularProgress';
import Credit from './steps/Credit';
import Box from '@mui/material/Box';
import Postpone from './steps/Postpone';
import SendPaymentInformation from './steps/SendPaymentInformation';
import SendSelectedInvoices from './steps/SendSelectedInvoices';
import SendDebtOverview from './steps/SendDebtOverview';
import { actionCreators as DebtorClaimsStoreActionCreators } from '../../../store/DebtorClaimsStore';
import Objection from './steps/Objection';
import { colors } from '../../../colors';
import { Button } from '@mui/material';
import { LeapClaim, SelectedDebtorClaim } from '../../../api/client';
import { SxProps, Theme } from '@mui/material';


interface PropsFromState {
  step: number;
  isSummary: boolean;
  isSubmitting: boolean;
  hasSelectedDebtorClaims: boolean;
  hasConversationSupervisorActions: boolean;
  selectedDebtorClaims: SelectedDebtorClaim[];
  activeLeapClaims: LeapClaim[];
}

interface PropsFromDispatch {
  setConversationSupervisorStep: typeof actionCreators.setSupervisorStep;
  clearDebtorClaimsSelection: typeof DebtorClaimsStoreActionCreators.clearDebtorClaimsSelection;
  toggleDebtorClaimSelection: typeof DebtorClaimsStoreActionCreators.toggleDebtorClaimSelection;
}

type ComponentProps = PropsFromState & PropsFromDispatch;

const claimButtonSx: SxProps<Theme> = {
  color: colors.black,
  backgroundColor: colors.lightGray,
  borderStyle: 'dotted',
  borderColor: colors.black,
  padding: '0 6px',
  margin: 0,
  '&:hover': {
    borderColor: colors.black,
  },
};

class ConversationSupervisor extends React.Component<ComponentProps> {

  handleSelectAll = () => {
    const selectedKeys = new Set(
      this.props.selectedDebtorClaims.map(c => `${c.externalReferenceId}::${c.number}`)
    );
    this.props.activeLeapClaims
      .filter(c => !selectedKeys.has(`${c.externalReferenceId}::${c.number}`))
      .forEach(c => this.props.toggleDebtorClaimSelection({
        providerSystem: c.providerSystem,
        number: c.number!.toString(),
        type: c.type!.toString(),
        externalReferenceId: c.externalReferenceId!,
      }));
  }

  handleUnselectAll = () => {
    this.props.clearDebtorClaimsSelection();
  }

  public render() {
    const stepComponents = [
      { name: Step.Start.toString(), component: <ConversationStart hasConversationSupervisorActions={this.props.hasConversationSupervisorActions} /> },
      { name: Step.SendPaymentInformation.toString(), component: <SendPaymentInformation /> },
      { name: Step.SendDebtOverview.toString(), component: <SendDebtOverview /> },
      { name: Step.SendSelectedInvoices.toString(), component: <SendSelectedInvoices /> },
      { name: Step.Objection.toString(), component: <Objection /> },
      { name: Step.Credit.toString(), component: <Credit /> },
      { name: Step.Postpone.toString(), component: <Postpone /> },
      { name: Step.OtherReason.toString(), component: <OtherReason /> },
      { name: Step.Summary.toString(), component: <ConversationSummary /> },
    ];

    return (
      <Box sx={{
        backgroundColor: 'background.default',
        minHeight: '442px',
        boxShadow: '0 2px 4px 0 rgba(0, 0, 0, 0.5)',
        padding: '27px',
        minWidth: '440px',
        maxHeight: '840px',
        display: 'flex',
        flexDirection: 'column',
        '& .step-progress': { flex: 1, display: 'flex', flexDirection: 'column' },
        '& .multi-step': { flex: 1, display: 'flex', flexDirection: 'column' },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }} >
        <Typography sx={{
          fontSize: '18px',
          fontWeight: 'bold',
          letterSpacing: '0.4px',
        }}>
          Samtaleveileder{this.props.isSummary ? ' oppsummering' : ''}
        </Typography>
          {
            this.props.step !== Step.Summary ?
            <Box sx={{ display: 'flex', gap: 1, justifySelf: 'end' }}>
                <Button size="small" variant="outlined" onClick={this.handleSelectAll} sx={claimButtonSx}>
                  Marker aktive krav
                </Button>
                <Button size="small" variant="outlined" onClick={this.handleUnselectAll} sx={claimButtonSx}>
                  Fjern alle haker
                </Button>
              </Box>
              : null
          }
      </Box>
        {!this.props.isSubmitting &&
          <StepZilla
            steps={stepComponents}
            showNavigation={false}
            showSteps={false}
            stepsNavigation={false}
            dontValidate={true}
            startAtStep={this.props.step}
            preventEnterSubmission={true}
            onStepChange={step => this.props.setConversationSupervisorStep(step)}
          />
        }
        {this.props.isSubmitting &&
          <CircularProgress size={75} sx={{
            progress: {
            margin: '0 auto',
            display: 'block',
            marginTop: '30px',
          }}} />
        }
      </Box>
    );
  }
}

const mapStateToProps = (state: ApplicationState): PropsFromState => ({
  step: state.conversationSupervisor.step,
  isSummary: state.conversationSupervisor.step === Step.Summary,
  isSubmitting: state.conversationSupervisor.isSubmitting,
  hasSelectedDebtorClaims: hasSelectedDebtorClaims(state.debtorClaims),
  hasConversationSupervisorActions: state.conversationSupervisor.conversation.actions!.length > 0,
  selectedDebtorClaims: state.debtorClaims.selectedDebtorClaims,
  activeLeapClaims: getEligibleActiveLeapClaimsForCurrentStep(state),
});

const mapDispatchToProps: PropsFromDispatch = {
  setConversationSupervisorStep: actionCreators.setSupervisorStep,
  clearDebtorClaimsSelection: DebtorClaimsStoreActionCreators.clearDebtorClaimsSelection,
  toggleDebtorClaimSelection: DebtorClaimsStoreActionCreators.toggleDebtorClaimSelection,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(ConversationSupervisor);
